import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import {
  BulkUpdateProvider,
  ControlsProvider,
  ViewLayoutProvider,
  ViewTemplateProvider,
  WaitForViewLayout,
} from '@shared/components/filter/state';
import { LoadingIndicator } from '@shared/components/loading';
import { EMPTY_FILTER_ONE_OF } from '@shared/data/cards/CardsEntityService';
import { useWorkflowEntityEvent } from '@shared/data/cards/hooks/WorkflowEntityEvents';
import { useStaggeredRefresh } from '@shared/data/cards/hooks/useStaggeredRefresh';
import { UpgradeModalProvider } from '@shared/scenes/billing';
import { OpenEntityOptions } from '@shared/scenes/cards/collectionView/hooks';
import { CardChannelProvider } from '@shared/scenes/cards/context';
import { CardList } from '@shared/scenes/cards/listView/components/CardList';
import { useCardListView } from '@shared/scenes/cards/listView/hooks/CardListViewHooks';
import { useCountWorkflowEntities } from '@shared/scenes/dashboard/hooks/useCountWorkflowEntities';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { ReferencedInComponent } from '@shared/scenes/workflows/config';
import {
  WorkspaceDetail,
  useViewTemplate,
  useWorkflowTemplate,
  useWorkspaceDetails,
} from '@shared/scenes/workflows/hooks';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { RouteName, RouteParamMapping, useNavigator } from '@shared/util/navigation';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';
import { ChannelPermissionsProvider } from '@shared/util/permissions';
import { AddMoreButton } from './AddMoreButton';
import { EditFieldContainer } from './EditFieldContainer';
import classNames from './ReferencedInEditField.module.css';

const COUNT_REFETCH_TIMEOUT_MS = 500;

interface ReferencedInEditFieldProps {
  component: ReferencedInComponent;
  entityId: string;
  style?: CSSProperties;
}

export function ReferencedInEditField({ component, entityId, style }: ReferencedInEditFieldProps) {
  const [showingList, setShowingList] = useState(false);
  const {
    workspaceDetails,
    countLoading,
    workflowDetailsLoading,
    referencedWorkflow,
    count,
    viewTemplate,
    navigateToCreate,
  } = useWorkflowReferencedInComponent(component, entityId);

  const workspaceName =
    component.label ??
    workspaceDetails.get(referencedWorkflow?.id ?? '')?.name ??
    referencedWorkflow?.name;
  const workspaceId = workspaceDetails.get(referencedWorkflow?.id ?? '')?.id;
  const workspaceImageUrl =
    workspaceDetails.get(referencedWorkflow?.id ?? '')?.imageUrl ??
    referencedWorkflow?.channelDefaultConfiguration?.imageUrl;

  const isMisconfigured =
    !component.referencedFromWorkflowTemplateId ||
    !component.relatedCardComponentId ||
    !component.referencingViewTemplateId;
  const isReadOnly = isMisconfigured;

  const loading = countLoading || workflowDetailsLoading;
  const showCount = !countLoading && !isEmpty(workspaceName);
  const printContent =
    !countLoading && count != null && count > 0
      ? i18n('card.blocks.inCards.cards', { count, max: 10000 })
      : undefined;

  const anchorName = getAnchorName('referenced-in', workspaceId ?? 0);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      label={i18n('workflowTemplate.component.type.label.referencedIn')}
      iconUrl={workspaceImageUrl}
      onPress={isReadOnly ? undefined : () => setShowingList(prev => !prev)}
      style={style}
      isGrayScale={isMisconfigured}
      printContent={printContent}
      subfooter={
        <WarningSubfooter
          showWarning={isMisconfigured}
          message={i18n('workflowTemplate.component.error.misconfigured')}
        />
      }
      footer={
        <AddMoreButton
          component={component}
          onPress={navigateToCreate}
          label={i18n('workflowTemplate.component.referencedIn.createCard')}
        />
      }
    >
      <View row paddingVertical={1} className={classNames.field}>
        <View>
          <Text size="body1">{workspaceName ?? ' '}</Text>
          <Text size="body2">
            {!showCount && ' '}
            {showCount && i18n('card.blocks.inCards.cards', { count: count ?? 0, max: 10000 })}
          </Text>
        </View>
        {loading && <LoadingIndicator paddingHorizontal={0.5} />}
        {!loading && <Icon name="arrow-point-right" gridSize={2.5} />}
      </View>
      {showingList && !isNil(referencedWorkflow) && (
        <AnchoredSheet
          anchorName={anchorName}
          onClose={() => setShowingList(false)}
          initialSize={'full'}
          title={workspaceName ?? ''}
          style={{ minHeight: grid(28) }}
        >
          <View className={classNames.listContainer}>
            <ReferencedWorkflowCollectionView
              workflowTemplate={referencedWorkflow}
              viewTemplate={viewTemplate}
              baseNavigationPath={workspaceDetails.get(referencedWorkflow?.id)?.baseNavigationPath}
              component={component}
              workspaceId={workspaceId}
              entityId={entityId}
              closeSheet={() => setShowingList(false)}
            />
          </View>
        </AnchoredSheet>
      )}
    </EditFieldContainer>
  );
}

interface ReferencedWorkflowCollectionViewProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate?: API.ViewTemplateFragment;
  component: ReferencedInComponent;
  closeSheet: () => void;
  baseNavigationPath?: string;
  workspaceId?: number;
  entityId: string;
}

function ReferencedWorkflowCollectionView({
  workflowTemplate,
  viewTemplate,
  component,
  baseNavigationPath,
  workspaceId,
  entityId,
  closeSheet,
}: ReferencedWorkflowCollectionViewProps) {
  if (isNil(viewTemplate)) {
    return (
      <View padding={2}>
        <Text size="body1" style={{ textAlign: 'center' }}>
          {i18n('builder.referencedInOptions.noView')}
        </Text>
      </View>
    );
  }

  // Exclude any existing filter for relatedCardComponentId from the view template so the
  // entityId filter below is never overridden by a duplicate entry at a later position.
  const existingFilters = getFiltersFromViewTemplate(viewTemplate).filter(
    f => f.componentId !== component.relatedCardComponentId,
  );

  const viewTemplateWithFilter: API.ViewTemplateFragment = {
    ...viewTemplate,
    collectionCriteria: {
      filterByComponents: [
        {
          componentId: component.relatedCardComponentId,
          scalarKey: null,
          value: {
            ...EMPTY_FILTER_ONE_OF,
            containsString: { values: [entityId], negate: false },
          },
        },
        ...existingFilters,
      ],
      sortByComponents: viewTemplate?.collectionCriteria?.sortByComponents?.map(omitTypeName) ?? [],
      groups: [],
    },
  };
  return (
    <ChannelPermissionsProvider channelId={workspaceId}>
      <ViewTemplateProvider
        key={`${viewTemplateWithFilter.id}-${entityId}`}
        workflowTemplateId={workflowTemplate.id}
        viewTemplate={viewTemplateWithFilter}
      >
        <BulkUpdateProvider
          viewTemplate={viewTemplateWithFilter}
          workflowTemplateId={workflowTemplate.id}
          channelId={workspaceId}
        >
          <ControlsProvider workflowTemplateId={workflowTemplate.id}>
            <UpgradeModalProvider>
              <ViewLayoutProvider
                workflowTemplateId={workflowTemplate.id}
                viewTemplateId={viewTemplateWithFilter.id}
              >
                <WaitForViewLayout>
                  <ReferencedWorkflowListView
                    workflowTemplate={workflowTemplate}
                    baseNavigationPath={baseNavigationPath ?? ''}
                    viewTemplate={viewTemplate}
                    workspaceId={workspaceId}
                    closeSheet={closeSheet}
                  />
                </WaitForViewLayout>
              </ViewLayoutProvider>
            </UpgradeModalProvider>
          </ControlsProvider>
        </BulkUpdateProvider>
      </ViewTemplateProvider>
    </ChannelPermissionsProvider>
  );
}

interface ReferencedWorkflowListViewProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  baseNavigationPath: string;
  viewTemplate: API.ViewTemplateFragment;
  workspaceId?: number;
  closeSheet: () => void;
}

function ReferencedWorkflowListView({
  workflowTemplate,
  baseNavigationPath,
  viewTemplate,
  workspaceId,
  closeSheet,
}: ReferencedWorkflowListViewProps) {
  const {
    entities,
    openEntity,
    getIsCardBeforePaidCutoff,
    hasMoreCards,
    fetchMore,
    isMoreCardsLoading,
    isCardsLoading,
    canUpdateCard,
  } = useCardListView(workflowTemplate?.id, viewTemplate?.id, workspaceId ?? 0, baseNavigationPath);
  const openEntityAndClose = useCallback(
    (options: OpenEntityOptions) => {
      openEntity({
        ...options,
        backTooltip: i18n('card.navigation.returnToReferencedCard'),
      });
      closeSheet();
    },
    [closeSheet, openEntity],
  );

  if (canUpdateCard) {
    return (
      <CardChannelProvider channelSlug={baseNavigationPath ?? ''}>
        <View paddingVertical={1} paddingHorizontal={0.5}>
          <CardList
            canUpdateCard={canUpdateCard}
            workflowTemplate={workflowTemplate}
            viewTemplate={viewTemplate}
            entities={entities}
            openEntity={openEntityAndClose}
            getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
            hasMore={hasMoreCards}
            showMore={fetchMore}
            isMoreLoading={isMoreCardsLoading}
            isCardsLoading={isCardsLoading}
            isGroup={false}
            hasOverflowMenu={false}
          />
        </View>
      </CardChannelProvider>
    );
  }
  return (
    <View paddingVertical={1} paddingHorizontal={0.5}>
      <CardList
        canUpdateCard={canUpdateCard}
        workflowTemplate={workflowTemplate}
        viewTemplate={viewTemplate}
        entities={entities}
        openEntity={openEntityAndClose}
        getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
        hasMore={hasMoreCards}
        showMore={fetchMore}
        isMoreLoading={isMoreCardsLoading}
        isCardsLoading={isCardsLoading}
        isGroup={false}
        hasOverflowMenu={false}
      />
    </View>
  );
}

function useWorkflowReferencedInComponent(
  component: ReferencedInComponent,
  entityId: string,
): {
  workspaceDetails: Map<string, WorkspaceDetail>;
  countLoading: boolean;
  workflowDetailsLoading: boolean;
  viewTemplate?: API.ViewTemplateFragment;
  viewTemplateLoading: boolean;
  referencedWorkflow?: API.WorkflowTemplateFragment;
  navigateToCreate: () => void;
  count?: number;
} {
  const referencedFromWorkflowTemplate = useWorkflowTemplate(
    component.referencedFromWorkflowTemplateId,
  );
  const navigator = useNavigator();
  const workspaceDetails = useWorkspaceDetails();

  const { viewTemplate, loading: viewTemplateLoading } = useViewTemplate(
    component.referencedFromWorkflowTemplateId,
    component.referencingViewTemplateId,
  );

  const navigateToCreate = useCallback(() => {
    const navigationPath = workspaceDetails.get(
      referencedFromWorkflowTemplate.workflowTemplate?.id ?? '',
    )?.baseNavigationPath;
    if (!isNil(navigationPath)) {
      const navEndpoint: RouteName = 'CardCreate';
      const navOptions: RouteParamMapping['CardCreate'] = {
        channelSlug: navigationPath,
        fieldOverrides: {
          [component.relatedCardComponentId]: [{ id: entityId }],
        },
      };

      navigator.navigate(navEndpoint, navOptions);
    }
  }, [
    entityId,
    component.relatedCardComponentId,
    navigator,
    referencedFromWorkflowTemplate.workflowTemplate?.id,
    workspaceDetails,
  ]);

  const input = useMemo(() => {
    // Exclude any existing filter for relatedCardComponentId so the entityId filter isn't
    // overridden by a duplicate entry coming from the view template.
    const existingFiltersForCount = getFiltersFromViewTemplate(viewTemplate).filter(
      f => f.componentId !== component.relatedCardComponentId,
    );
    return {
      workflowTemplateId: component.referencedFromWorkflowTemplateId,
      filterByComponents: [
        {
          componentId: component.relatedCardComponentId,
          value: {
            containsCurrentUser: null,
            containsNumber: null,
            containsString: { values: [entityId], negate: false },
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            isEmpty: null,
            numberIs: null,
            stringWildcard: null,
          },
        },
        ...existingFiltersForCount,
      ],
    };
  }, [
    component.referencedFromWorkflowTemplateId,
    component.relatedCardComponentId,
    entityId,
    viewTemplate,
  ]);

  const [canFetchCount, setCanFetchCount] = useState(false);
  const {
    totalResults,
    loading: countLoading,
    refetch: refetchCount,
  } = useCountWorkflowEntities({
    input,
    skip: isNil(viewTemplate) || !canFetchCount,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const indexDelay = setTimeout(() => {
      setCanFetchCount(true);
    }, COUNT_REFETCH_TIMEOUT_MS);
    return () => clearTimeout(indexDelay);
  }, []);

  // People notice when this count is off, and only a count query can tell when a card that was
  // never seen here drops its link, so every relevant event refreshes it - staggered, as one
  // field's count costs far less than a collection view's groups.
  const refreshCount = useStaggeredRefresh(() => refetchCount(), { key: input });

  useWorkflowEntityEvent((data: API.WorkflowEntitySubscription) => {
    if (!canFetchCount) {
      return;
    }
    const { type, entities } = data.event;
    const hasRelevantEntity = entities.some(e =>
      isReferencedInEntity({ entity: e, eventType: type, component, entityId }),
    );
    if (hasRelevantEntity) {
      refreshCount();
    }
  });

  return {
    workspaceDetails,
    countLoading: countLoading || !canFetchCount,
    workflowDetailsLoading: referencedFromWorkflowTemplate.loading,
    referencedWorkflow: referencedFromWorkflowTemplate.workflowTemplate,
    count: totalResults,
    viewTemplate,
    viewTemplateLoading,
    navigateToCreate,
  };
}

function getFiltersFromViewTemplate(
  viewTemplate?: API.ViewTemplateFragment,
): API.FilterByComponentFragment[] {
  return (
    viewTemplate?.collectionCriteria?.filterByComponents?.filter(notNil).map(omitTypeName) ?? []
  );
}

function isReferencedInEntity({
  entity,
  eventType,
  component,
  entityId,
}: {
  entity: API.WorkflowEntitySubscription['event']['entities'][number];
  eventType: string;
  component: ReferencedInComponent;
  entityId: string;
}): boolean {
  if (entity.workflowTemplateId !== component.referencedFromWorkflowTemplateId) {
    return false;
  }
  // For new cards, only refetch if the card actually references the current
  // entity — avoids unnecessary refetches when unrelated cards are created
  // in the same workflow.
  if (eventType === 'WORKFLOW_ENTITY_CREATED') {
    const relatedField = entity.fields?.[component.relatedCardComponentId];
    return Array.isArray(relatedField) && relatedField.some(item => item.id === entityId);
  }
  // For updates/deletes the reference may have just been removed, so
  // refetch conservatively rather than risk a stale count.
  return true;
}
